import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import LEARN_ARTICLES from "../../data/learnArticles";
import "./Learn.css";

const BY_SLUG = Object.fromEntries(LEARN_ARTICLES.map((a) => [a.slug, a]));

const LearnArticlePage = () => {
  const { slug } = useParams();
  const article = BY_SLUG[slug];

  // Unknown slug -- send to the hub instead of rendering an empty page.
  if (!article) {
    return <Navigate to="/learn" replace />;
  }

  const related = LEARN_ARTICLES.filter(
    (a) => a.category === article.category && a.slug !== article.slug
  );

  return (
    <div className="learn-detail-container">
      <article className="learn-article-wrapper">
        <header className="learn-header">
          <div className="learn-header-inner">
            <nav className="learn-breadcrumb">
              <Link to="/" className="learn-breadcrumb-link">Home</Link>
              <span className="learn-breadcrumb-sep">/</span>
              <Link to="/learn" className="learn-breadcrumb-link">Learn</Link>
              <span className="learn-breadcrumb-sep">/</span>
              <span className="learn-breadcrumb-current">{article.heading}</span>
            </nav>

            <span className="learn-category-badge">{article.category}</span>
            <h1 className="learn-title">{article.heading}</h1>
            <p className="learn-summary">{article.summary}</p>
          </div>
        </header>

        <div className="learn-content-wrapper">
          <div className="learn-content">
            {article.comparisonTable ? (
              <>
                {article.intro.map((para, i) => (
                  <p className="learn-intro-para" key={i}>{para}</p>
                ))}

                {article.supplierNote && (
                  <section className="learn-section learn-supplier-note">
                    <p>{article.supplierNote}</p>
                  </section>
                )}

                <section className="learn-section">
                  <h2>Side-by-Side Comparison</h2>
                  <div className="learn-compare-wrap">
                    <table className="learn-compare-table">
                      <thead>
                        <tr>
                          <th>Example</th>
                          <th>Technology</th>
                          <th>Contrast &amp; Viewing</th>
                          <th>Relative Cost</th>
                          <th>Best Suited For</th>
                        </tr>
                      </thead>
                      <tbody>
                        {article.comparisonTable.map((row) => (
                          <tr key={row.type}>
                            <td className="learn-compare-cell-img">
                              <img
                                src={row.image}
                                alt={`${row.name} (${row.type}) segmented LCD sample`}
                                className="learn-compare-img"
                              />
                            </td>
                            <td className="learn-compare-cell-type">
                              <span className="learn-compare-tag">{row.type}</span>
                              {row.name}
                              <p className="learn-compare-desc">{row.description}</p>
                            </td>
                            <td>{row.contrast}</td>
                            <td>{row.cost}</td>
                            <td>{row.bestFor}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <span className="learn-compare-scroll-hint">Swipe to see more →</span>
                </section>

                {article.howToChoose && (
                  <section className="learn-section learn-bestfor">
                    <h2>Which Should You Choose?</h2>
                    <p>{article.howToChoose}</p>
                  </section>
                )}
              </>
            ) : (
              <>
                <section className="learn-section">
                  <h2>What It Is</h2>
                  <p>{article.whatItIs}</p>
                </section>

                <section className="learn-section">
                  <h2>How It Works</h2>
                  <p>{article.howItWorks}</p>
                </section>

                {(article.pros.length > 0 || article.cons.length > 0) && (
                  <section className="learn-section learn-proscons">
                    {article.pros.length > 0 && (
                      <div className="learn-pros">
                        <h3>Pros</h3>
                        <ul>
                          {article.pros.map((p, i) => (
                            <li key={i}>{p}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {article.cons.length > 0 && (
                      <div className="learn-cons">
                        <h3>Cons</h3>
                        <ul>
                          {article.cons.map((c, i) => (
                            <li key={i}>{c}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </section>
                )}

                <section className="learn-section learn-bestfor">
                  <h2>Best For</h2>
                  <p>{article.bestFor}</p>
                </section>
              </>
            )}

            {related.length > 0 && (
              <section className="learn-related">
                <h3 className="learn-related-heading">More on {article.category}</h3>
                <div className="learn-related-grid">
                  {related.map((r) => (
                    <Link to={`/learn/${r.slug}`} key={r.slug} className="learn-related-card">
                      <span className="learn-related-pill">{r.heading}</span>
                      <span className="learn-related-summary">{r.summary}</span>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <div className="learn-cta">
              <h3 className="learn-cta-title">Need a Segmented LCD Built to This Spec?</h3>
              <p className="learn-cta-desc">
                Every BIBA segmented display is engineered from a blank sheet around
                your product -- the layout, icons and connector are drawn to match
                your exact requirement.
              </p>
              <div className="learn-cta-actions">
                <Link to="/category/passive-displays/segmented-display" className="learn-btn-outline">
                  ← Back to Segmented LCD Displays
                </Link>
                <a href="/contact#rfq" className="learn-btn-solid">Get a Quote</a>
              </div>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

export default LearnArticlePage;
